import { Stack } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { useScrollRestoration } from "@src/appV2/lib/utils/useScrollRestoration";
import type { HiringFeedsResponse } from "@src/appV2/redesign/HiringFeed/api/hiringFeed.schema";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { type DiscoveryPlacementsResponse } from "@src/appV2/redesign/Placements/api/useFetchDiscoveryPlacements";
import { JobsApplicationsTabs } from "@src/appV2/redesign/Placements/components/JobsApplicationsTabs";
import { useMaxReferralBonusAmount } from "@src/appV2/Referral/Bonus/flags";
import { WORKER_REFERRALS_SHARE_LINK_PATH_FULL } from "@src/appV2/Referral/path";
import { type Worker } from "@src/appV2/Worker/api/types";
import { type UseInfiniteQueryResult } from "@tanstack/react-query";
import { differenceInDays, parseISO } from "date-fns";
import { useEffect } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";

import { Button } from "../../components/Button";
import { ModalRoute } from "../../Navigation/ModalRoute";
import { OnboardingRequirementsBanner } from "../../PlacementCandidate/components/OnboardingRequirementsBanner";
import { AccountDeactivatedCallout } from "../../Worker/AccountDeactivatedCallout";
import {
  transformPaginatedInterviews,
  useInterviewAttendanceOutcome,
} from "../hooks/useInterviewAttendanceOutcome";
import { PLACEMENTS_BASE_PATH } from "../paths";
import { type GetPlacementsForWorkplaceQuery } from "../types/fetchPlacements.schema";
import { useIsHiringReferralEnabled } from "../useIsHiringReferralEnabled";
import { AppliedPlacementListView } from "./AppliedPlacementListView";
import { InterviewAttendanceFeedbackDialog } from "./InterviewAttendanceFeedback/InterviewAttendanceFeedbackDialog";
import { OpenPlacementListView } from "./OpenPlacementListView";
import { RestrictedAccessBanner } from "./RestrictedAccessBanner";

interface PlacementsListContainerProps {
  placementCandidate: PlacementCandidate;
  worker: Worker;
  filter: GetPlacementsForWorkplaceQuery["filter"];
  setFilter: (filter: GetPlacementsForWorkplaceQuery["filter"]) => void;
  hiringFeedResults: UseInfiniteQueryResult<HiringFeedsResponse>;
  isDiscoveryMigrationEnabled: boolean;
  discoveryResults: UseInfiniteQueryResult<DiscoveryPlacementsResponse>;
  showApplications: boolean;
  setShowApplications: (value: boolean) => void;
}

export function PlacementsListContainer(props: PlacementsListContainerProps) {
  const {
    placementCandidate,
    worker,
    filter,
    setFilter,
    showApplications,
    setShowApplications,
    hiringFeedResults,
    isDiscoveryMigrationEnabled,
    discoveryResults,
  } = props;

  const match = useRouteMatch();
  const history = useHistory();

  useScrollRestoration({ key: "jobs-tab", containerId: "jobs-list-container" });

  const [interviewAttendanceFeedbackDismissedAt, setInterviewAttendanceFeedbackDismissedAt] =
    useLocalStorage<string | undefined>("interview-attendance-feedback-dismissed-at", undefined);

  const { data: interviewsDataWithPages } = useInterviewAttendanceOutcome({
    workerId: worker.userId,
    withDismissed: true,
  });
  const interviews = transformPaginatedInterviews(interviewsDataWithPages?.pages, worker.tmz ?? "");

  const maxReferralBonusAmount = useMaxReferralBonusAmount("postOnboardingBonusInDollars");
  const isHiringReferralEnabled = useIsHiringReferralEnabled();

  useEffect(() => {
    const lastDismissed = interviewAttendanceFeedbackDismissedAt
      ? differenceInDays(new Date(), parseISO(interviewAttendanceFeedbackDismissedAt))
      : undefined;
    if (interviews && interviews.length > 0 && (lastDismissed === undefined || lastDismissed > 1)) {
      history.push(
        `${RootPaths.APP_V2_HOME}/${PLACEMENTS_BASE_PATH}/interviews/outcome?source=auto-popup`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviews?.length]);

  return (
    <Stack
      sx={{
        paddingY: 5,
        backgroundColor: (theme) => theme.palette.background.tertiary,
        flex: 1,
      }}
      id="jobs-list-container"
    >
      <Stack>
        <Stack spacing={6} sx={{ paddingX: 5 }}>
          {isHiringReferralEnabled && (
            <Stack justifyContent="flex-end" direction="row">
              <Button
                startIconType="share"
                size="small"
                variant="contained"
                sx={{
                  backgroundColor: (theme) => theme.palette.intent?.success.background,
                  color: (theme) => theme.palette.intent?.success.text,
                }}
                href={WORKER_REFERRALS_SHARE_LINK_PATH_FULL}
                onClick={() => {
                  logEvent(APP_V2_USER_EVENTS.PLACEMENT_PAGE_REFER_FRIEND_CLICKED, {
                    placementCandidateId: placementCandidate.id,
                    workerId: worker.userId,
                  });
                }}
              >
                Refer friend, earn ${maxReferralBonusAmount}
              </Button>
            </Stack>
          )}
          {placementCandidate.isRestricted && placementCandidate.mostRecentBan ? (
            <RestrictedAccessBanner
              placementCandidateBan={placementCandidate.mostRecentBan}
              workerTimezone={worker.tmz}
            />
          ) : undefined}
          <AccountDeactivatedCallout />
          <JobsApplicationsTabs
            showApplications={showApplications}
            onChange={(showApplications) => {
              logEvent(APP_V2_APP_EVENTS.PLACEMENT_CANDIDATE_JOBS_TAB_SWITCHED, {
                placementCandidateId: placementCandidate.id,
                workerId: worker.userId,
                showApplications,
              });
              setShowApplications(showApplications);
            }}
          />

          <OnboardingRequirementsBanner workerId={worker.userId} />

          {!showApplications && (
            <OpenPlacementListView
              placementCandidate={placementCandidate}
              worker={worker}
              filter={filter}
              setFilter={setFilter}
              showApplications={showApplications}
              hiringFeedResults={hiringFeedResults}
              isDiscoveryMigrationEnabled={isDiscoveryMigrationEnabled}
              discoveryResults={discoveryResults}
            />
          )}

          {showApplications && (
            <AppliedPlacementListView
              placementCandidate={placementCandidate}
              worker={worker}
              filter={filter}
              showApplications={showApplications}
            />
          )}
        </Stack>
      </Stack>
      <ModalRoute
        allowNestedPaths
        exact
        closeModalPath={match.url}
        path={`${match.url}/interviews/outcome`}
        render={({ modalState }) => {
          return (
            <InterviewAttendanceFeedbackDialog
              placementCandidate={placementCandidate}
              modalState={modalState}
              onClose={() => {
                setInterviewAttendanceFeedbackDismissedAt(new Date().toISOString());
              }}
            />
          );
        }}
      />
    </Stack>
  );
}
